'use client';
import { ChevronDown, HelpCircle, Search } from 'lucide-react';
import Link from 'next/link';
import React, { useEffect, useRef, useState } from 'react';

import GradientHeading from '@/components/shared/gradientHeading';

const FAQItem = ({ question, answer, index, isFirstItem }) => {
  const [isOpen, setIsOpen] = useState(isFirstItem);
  const [contentHeight, setContentHeight] = useState(0);
  const contentRef = useRef(null);

  // Measure content height for smooth animations
  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(isOpen ? contentRef.current.scrollHeight : 0);
    }
  }, [isOpen]);

  const toggleFAQ = () => {
    setIsOpen(!isOpen);
  };

  // Generate a stable ID for accessibility
  const questionId = `faq-question-${question.replace(/\s+/g, '-').toLowerCase()}`;
  const answerId = `faq-answer-${question.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <div
      className={`mb-4 overflow-hidden rounded-xl border border-gray-100 bg-white transition-all duration-300 ${
        isOpen ? 'shadow-md' : 'hover:border-gray-200'
      } dark:border-gray-700 dark:bg-gray-800`}
    >
      <button
        className="flex w-full cursor-pointer items-center justify-between px-6 py-5 text-left transition-colors"
        onClick={toggleFAQ}
        aria-expanded={isOpen}
        aria-controls={answerId}
        id={questionId}
      >
        <div className="flex items-center">
          <div
            className={`mr-4 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-colors ${
              isOpen
                ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
            }`}
          >
            <span className="text-sm font-medium">{index + 1}</span>
          </div>
          <h4 className="text-lg font-semibold text-gray-900 dark:text-white">{question}</h4>
        </div>
        <div
          className={`ml-4 flex-shrink-0 rounded-full p-1.5 transition-transform duration-300 ${
            isOpen ? 'rotate-180 bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-700'
          }`}
        >
          <ChevronDown
            className={`h-5 w-5 transition-colors ${
              isOpen ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'
            }`}
          />
        </div>
      </button>

      <div
        className="overflow-hidden transition-all duration-300"
        style={{ maxHeight: contentHeight }}
        aria-labelledby={questionId}
        id={answerId}
        role="region"
      >
        <div
          ref={contentRef}
          className="border-t border-gray-100 px-6 py-5 text-gray-600 dark:border-gray-700 dark:text-gray-300"
        >
          <p>{answer}</p>

          {/* Category-related FAQs get a CTA link */}
          {(question.toLowerCase().includes('topics') ||
            question.toLowerCase().includes('categor')) && (
            <div className="mt-4 border-t border-gray-100 pt-4 dark:border-gray-700">
              <Link
                prefetch={false}
                href="/categories"
                className="inline-flex items-center font-medium text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
              >
                Browse our content categories
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="ml-1.5 h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                  />
                </svg>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

let faqItems = [
  {
    category: 'general',
    question: 'What are the main topics covered on AllBlogs?',
    answer:
      "AllBlogs covers a diverse range of subjects organized into easy-to-browse categories. We have a special focus on providing inspiring and practical content related to Travel & Adventure, Food & Recipes, and Health & Wellness. You'll also find insightful articles on personal growth, technology, culture, finance, and more - all accessible through our category system.",
  },
  {
    category: 'content',
    question: 'How can I contribute to AllBlogs?',
    answer:
      "We welcome contributions from passionate writers. If you have expertise you'd like to share, please visit our 'Contribute' page for guidelines and submission information. We accept articles across all our content categories and are particularly interested in unique perspectives and expert insights.",
  },
  {
    category: 'general',
    question: 'Is AllBlogs free to use?',
    answer:
      'Yes, exploring and reading all our content on AllBlogs is completely free for everyone. You can browse articles across all categories without any subscription fees or paywalls.',
  },
  {
    category: 'account',
    question: 'Can I subscribe to the AllBlogs newsletter?',
    answer:
      'Absolutely! You can subscribe to our newsletter via the form on our website to get the latest updates, new posts, and exclusive content delivered to your inbox. Our newsletter often features highlights from various content categories and special thematic editions.',
  },
  {
    category: 'technical',
    question: 'How do I report an issue or give feedback?',
    answer:
      "If you encounter any technical issues or have feedback about our content or site, please reach out through our 'Contact Us' page. We appreciate your input and continuously work to improve the browsing experience across all our content categories.",
  },
  {
    category: 'content',
    question: 'How are your content categories organized?',
    answer:
      "Our categories are thoughtfully organized to help you find exactly what you're looking for. Each category contains related articles, and you can easily browse them from our main Categories page. We regularly review and update our category system to ensure it remains intuitive and comprehensive as our content grows.",
  },
  {
    category: 'general',
    question: 'Are there age restrictions for using AllBlogs?',
    answer:
      'AllBlogs is suitable for a general audience. However, we always recommend parental guidance for younger readers exploring online content. Some specific categories may contain content more appropriate for mature readers.',
  },
];

const FAQPage = ({ blogFAQs = [] }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredFAQs, setFilteredFAQs] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const faqSectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  // Define your FAQ items

  if (blogFAQs && blogFAQs.length > 0) {
    faqItems = blogFAQs;
  }

  // Intersection observer effect
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    if (faqSectionRef.current) {
      observer.observe(faqSectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Extract unique categories from FAQ items
  const categories = ['all', ...new Set(faqItems.map(item => item.category).filter(Boolean))];

  // Filter FAQs based on search query and active category
  useEffect(() => {
    const query = searchQuery.toLowerCase();

    let filtered = faqItems;

    // Apply category filter
    if (activeCategory !== 'all') {
      filtered = filtered.filter(item => item.category === activeCategory);
    }

    // Apply search filter
    if (query) {
      filtered = filtered.filter(
        item =>
          item.question.toLowerCase().includes(query) || item.answer.toLowerCase().includes(query),
      );
    }

    setFilteredFAQs(filtered);
  }, [searchQuery, activeCategory]);

  // Create the JSON-LD structured data
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  // Animation classes
  const getAnimationClass = () => {
    return isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0';
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(faqSchema),
        }}
      />
      <div
        ref={faqSectionRef}
        className={`mt-4 transform pb-8 transition-all duration-700 ${getAnimationClass()}`}
      >
        <div className="mb-8 text-center">
          <GradientHeading level={2} className="mb-4 text-3xl font-bold">
            Frequently Asked Questions
          </GradientHeading>
          <p className="mx-auto max-w-2xl text-gray-600 dark:text-gray-300">
            Find answers to common questions about AllBlogs, our content, and how to make the most
            of your experience.
          </p>
        </div>

        {/* Search and filter controls */}
        <div className="mb-8">
          <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4">
            {/* Search input */}
            <div className="relative flex-grow">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search questions..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="block w-full rounded-lg border border-gray-200 bg-white py-3 pr-3 pl-10 text-gray-700 focus:border-green-500 focus:ring-2 focus:ring-green-500 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:focus:border-green-500"
              />
            </div>

            {/* Category filter pills */}
            <div className="flex flex-wrap gap-2">
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    activeCategory === category
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* No results message */}
        {filteredFAQs.length === 0 && (
          <div className="my-8 rounded-lg border border-gray-200 bg-white p-8 text-center shadow-md dark:border-gray-700 dark:bg-gray-800">
            <HelpCircle className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
              No matching questions found
            </h3>
            <p className="mt-2 text-gray-600 dark:text-gray-300">
              Try adjusting your search or filter to find what you are looking for.
            </p>
            <button
              onClick={() => {
                setSearchQuery('');
                setActiveCategory('all');
              }}
              className="mt-4 rounded-md bg-green-600 px-4 py-2 text-white hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700"
            >
              Reset Filters
            </button>
          </div>
        )}

        {/* FAQ items */}
        <div className="space-y-1">
          {filteredFAQs.map((item, index) => (
            <FAQItem
              key={index}
              question={item.question}
              answer={item.answer}
              index={index}
              isFirstItem={index === 0}
            />
          ))}
        </div>

        {/* Still have questions */}
        <div className="mt-12 rounded-xl bg-gradient-to-r p-8 text-center shadow-lg dark:from-gray-800 dark:to-gray-900">
          <h3 className="mb-4 text-xl font-bold text-gray-900 dark:text-white">
            Still have questions?
          </h3>
          <p className="mb-6 text-gray-600 dark:text-gray-300">
            Can not find the answer you are looking for? Please reach out to our friendly team.
          </p>
          <Link
            prefetch={false}
            href="/contact"
            className="inline-flex items-center rounded-full bg-green-600 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700"
          >
            Contact Us
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="ml-2 h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14 5l7 7m0 0l-7 7m7-7H3"
              />
            </svg>
          </Link>
        </div>
      </div>
    </>
  );
};

export default FAQPage;
